/**
 * Generation Store
 *
 * Manages AI generation and background chat state including:
 * - Background chat conversations
 * - Completed chats
 * - Analyzer streaming state
 * - Active analyzer chat tracking
 *
 * @module store/generation/generationStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { createPersistConfig } from '../middleware/persistenceMiddleware';
import logger from '../../shared/services/logging/logger';

/**
 * Generation store state and actions
 *
 * @typedef {Object} GenerationState
 * @property {boolean} isGenerating - Whether generation is in progress
 * @property {boolean} isComplete - Whether generation is complete
 * @property {Array} backgroundChats - Active background chat conversations
 * @property {Array} completedChats - Completed chat conversations
 * @property {boolean} isAnalyzerStreaming - Whether analyzer is currently streaming
 * @property {string|null} activeAnalyzerChat - ID of active analyzer chat
 */

/**
 * Maximum age (ms) for persisted sessions before they're considered stale.
 * Sessions older than this are cleared on rehydration to prevent ghost entries.
 */
const STALE_SESSION_TTL = 2 * 60 * 60 * 1000; // 2 hours

export const useGenerationStore = create(
  persist(
  (set, get) => ({
  // State
  isGenerating: false,
  isComplete: false,
  backgroundChats: [],
  completedChats: [],
  isAnalyzerStreaming: false,
  activeAnalyzerChat: null,
  activeScriptGeneration: null, // { conversationId, projectData, route }
  xrayPendingJobs: [], // [{ jobId, testSetName, projectName }]

  // Actions

  /**
   * Set generation status
   * @param {boolean} isGenerating - Generation status
   */
  setIsGenerating: (isGenerating) => {
    logger.debug('Setting generation status', { isGenerating });
    set({ isGenerating });
  },

  /**
   * Set completion status
   * @param {boolean} isComplete - Completion status
   */
  setIsComplete: (isComplete) => {
    logger.debug('Setting completion status', { isComplete });
    set({ isComplete });
  },

  /**
   * Add a new background chat conversation
   * @param {string} conversationId - Unique conversation ID
   * @param {string} title - Chat title
   * @param {string} messageContent - Initial message content
   */
  addBackgroundChat: (conversationId, title, messageContent = '') => {
    const { backgroundChats } = get();

    // Don't add duplicates
    if (backgroundChats.some(chat => chat.conversationId === conversationId)) {
      logger.debug('Background chat already exists', { conversationId });
      return;
    }

    logger.info('Adding background chat', { conversationId, title });

    set({
      backgroundChats: [
        ...backgroundChats,
        {
          conversationId,
          title,
          messageContent,
          startedAt: Date.now()
        }
      ]
    });
  },

  /**
   * Remove a background chat conversation
   * @param {string} conversationId - Conversation ID to remove
   */
  removeBackgroundChat: (conversationId) => {
    logger.info('Removing background chat', { conversationId });

    set(state => ({
      backgroundChats: state.backgroundChats.filter(
        chat => chat.conversationId !== conversationId
      )
    }));
  },

  /**
   * Get background chat by ID
   * @param {string} conversationId - Conversation ID
   * @returns {Object|undefined} Background chat object or undefined
   */
  getBackgroundChat: (conversationId) => {
    const { backgroundChats } = get();
    return backgroundChats.find(chat => chat.conversationId === conversationId);
  },

  /**
   * Update background chat message content
   * @param {string} conversationId - Conversation ID
   * @param {string} messageContent - New message content
   */
  updateBackgroundChatContent: (conversationId, messageContent) => {
    logger.debug('Updating background chat content', { conversationId });

    set(state => ({
      backgroundChats: state.backgroundChats.map(chat =>
        chat.conversationId === conversationId
          ? { ...chat, messageContent }
          : chat
      )
    }));
  },

  /**
   * Mark chat as completed and move to completed chats
   * @param {string} conversationId - Conversation ID
   * @param {string} title - Chat title
   */
  markChatCompleted: (conversationId, title) => {
    logger.info('Marking chat as completed', { conversationId, title });

    set(state => ({
      backgroundChats: state.backgroundChats.filter(
        chat => chat.conversationId !== conversationId
      ),
      completedChats: [
        ...state.completedChats,
        {
          conversationId,
          title,
          completedAt: Date.now()
        }
      ]
    }));
  },

  /**
   * Clear a completed chat
   * @param {string} conversationId - Conversation ID to clear
   */
  clearCompletedChat: (conversationId) => {
    logger.debug('Clearing completed chat', { conversationId });

    set(state => ({
      completedChats: state.completedChats.filter(
        chat => chat.conversationId !== conversationId
      )
    }));
  },

  /**
   * Set analyzer streaming status
   * @param {boolean} isStreaming - Streaming status
   */
  setIsAnalyzerStreaming: (isStreaming) => {
    logger.debug('Setting analyzer streaming status', { isStreaming });
    set({ isAnalyzerStreaming: isStreaming });
  },

  /**
   * Set active analyzer chat ID
   * @param {string|null} chatId - Active chat ID or null
   */
  setActiveAnalyzerChat: (chatId) => {
    logger.debug('Setting active analyzer chat', { chatId });
    set({ activeAnalyzerChat: chatId });
  },

  /**
   * Set active script generation session (for sidebar "return to" link)
   * @param {Object|null} session - { conversationId, projectData, route } or null to clear
   */
  setActiveScriptGeneration: (session) => {
    logger.debug('Setting active script generation', { conversationId: session?.conversationId });
    set({
      activeScriptGeneration: session
        ? { ...session, _persistedAt: session._persistedAt || Date.now() }
        : null
    });
  },

  /**
   * Clear active script generation session
   */
  clearActiveScriptGeneration: () => {
    const current = get().activeScriptGeneration;
    if (current) {
      logger.info('Clearing active script generation', {
        conversationId: current.conversationId,
        caller: new Error().stack?.split('\n')[2]?.trim()
      });
    }
    set({ activeScriptGeneration: null });
  },

  addXRayJob: (jobId, testSetName, projectName) => {
    set(state => ({
      xrayPendingJobs: [...state.xrayPendingJobs, { jobId, testSetName, projectName, startedAt: Date.now() }]
    }));
  },

  removeXRayJob: (jobId) => {
    set(state => ({
      xrayPendingJobs: state.xrayPendingJobs.filter(j => j.jobId !== jobId)
    }));
  }
}),
    createPersistConfig('cliffi-generation-store', {
      /**
       * Only persist the fields needed to survive page refresh and reconnect.
       * Transient flags (isGenerating, isComplete, isAnalyzerStreaming, etc.)
       * are NOT persisted — they're session-scoped and rebuilt by listeners.
       */
      partialize: (state) => ({
        backgroundChats: state.backgroundChats,
        activeScriptGeneration: state.activeScriptGeneration,
        xrayPendingJobs: state.xrayPendingJobs,
      }),
      onRehydrateStorage: (state, error) => {
        if (error) return;
        if (!state) return;

        const now = Date.now();

        // Clear stale background chats (older than 2 hours)
        if (state.backgroundChats?.length > 0) {
          const fresh = state.backgroundChats.filter(
            chat => (now - (chat.startedAt || 0)) < STALE_SESSION_TTL
          );
          if (fresh.length !== state.backgroundChats.length) {
            logger.info('Cleared stale background chats on rehydration', {
              removed: state.backgroundChats.length - fresh.length
            });
            useGenerationStore.setState({ backgroundChats: fresh });
          }
        }

        // Clear stale activeScriptGeneration (older than 2 hours)
        if (state.activeScriptGeneration?.workflowSnapshot) {
          const sessionAge = now - (state.activeScriptGeneration._persistedAt || 0);
          if (sessionAge > STALE_SESSION_TTL) {
            logger.info('Cleared stale script generation session on rehydration', {
              ageMinutes: Math.round(sessionAge / 60000)
            });
            useGenerationStore.setState({ activeScriptGeneration: null });
          }
        }

        // Clear stale XRay jobs (older than 2 hours)
        if (state.xrayPendingJobs?.length > 0) {
          const fresh = state.xrayPendingJobs.filter(
            job => (now - (job.startedAt || 0)) < STALE_SESSION_TTL
          );
          if (fresh.length !== state.xrayPendingJobs.length) {
            logger.info('Cleared stale XRay jobs on rehydration', {
              removed: state.xrayPendingJobs.length - fresh.length
            });
            useGenerationStore.setState({ xrayPendingJobs: fresh });
          }
        }
      }
    })
  )
);

// Granular selector hooks

/**
 * Get generation status
 * @returns {boolean} Whether generation is in progress
 */
export const useIsGenerating = () =>
  useGenerationStore(state => state.isGenerating);

/**
 * Get completion status
 * @returns {boolean} Whether generation is complete
 */
export const useIsComplete = () =>
  useGenerationStore(state => state.isComplete);

/**
 * Get background chats list
 * @returns {Array} Background chats array
 */
export const useBackgroundChats = () =>
  useGenerationStore(state => state.backgroundChats);

/**
 * Get completed chats list
 * @returns {Array} Completed chats array
 */
export const useCompletedChats = () =>
  useGenerationStore(state => state.completedChats);

/**
 * Get analyzer streaming status
 * @returns {boolean} Whether analyzer is streaming
 */
export const useIsAnalyzerStreaming = () =>
  useGenerationStore(state => state.isAnalyzerStreaming);

/**
 * Get active analyzer chat ID
 * @returns {string|null} Active analyzer chat ID
 */
export const useActiveAnalyzerChat = () =>
  useGenerationStore(state => state.activeAnalyzerChat);

/**
 * Get active script generation session
 * @returns {Object|null} Active script generation session or null
 */
export const useActiveScriptGeneration = () =>
  useGenerationStore(state => state.activeScriptGeneration);

export const useXRayPendingJobs = () =>
  useGenerationStore(state => state.xrayPendingJobs);

/**
 * Get all generation actions
 * @returns {Object} Generation actions
 */
export const useGenerationActions = () =>
  useGenerationStore(
    useShallow(state => ({
      setIsGenerating: state.setIsGenerating,
      setIsComplete: state.setIsComplete,
      addBackgroundChat: state.addBackgroundChat,
      removeBackgroundChat: state.removeBackgroundChat,
      getBackgroundChat: state.getBackgroundChat,
      updateBackgroundChatContent: state.updateBackgroundChatContent,
      markChatCompleted: state.markChatCompleted,
      clearCompletedChat: state.clearCompletedChat,
      setIsAnalyzerStreaming: state.setIsAnalyzerStreaming,
      setActiveAnalyzerChat: state.setActiveAnalyzerChat,
      setActiveScriptGeneration: state.setActiveScriptGeneration,
      clearActiveScriptGeneration: state.clearActiveScriptGeneration
    }))
  );
